import { useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { styled, type Style } from 'inlines'
import { Text } from '../typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import { TabRow } from './internal/TabRow'
import { MoreDropdownTab } from './internal/MoreDropdownTab'
import { TabsContext } from './internal/TabsContext'

const DEFAULT_MAX_VISIBLE_TABS = 5

export type TabItem<T extends string> = {
  value: T
  label: string
  isVisible?: boolean
}

export type TabsGroupProps<T extends string> = {
  tabs: TabItem<T>[]
  maxVisibleTabs?: number
  moreDropdownLabel?: string
  selectedTab: T
  onSelectTab: (key: T) => void
  style?: Style
}

type InternalTabProps<T extends string> = Omit<TabItem<T>, 'isVisible'> & {
  onSelectTab: (key: T) => void
}

const Tab = <T extends string>({
  value,
  label,
  onSelectTab,
}: InternalTabProps<T>) => {
  const context = useContext(TabsContext)

  if (!context) {
    throw new Error('Tabs content must be used within TabsGroup')
  }

  const { selectedTab } = context
  const isSelected = selectedTab === value

  const tabStyle: Style = {
    padding: '1.5rem',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--primary-blue)',
    },
    borderBottom: `2px ${
      isSelected ? 'var(--primary-blue)' : 'transparent'
    } solid`,
  }

  const textStyle: Style = {
    wordWrap: 'break-word',
    whiteSpace: 'nowrap',
    color: isSelected ? 'var(--primary-blue)' : 'var(--shades-blue-300)',
    '&:hover': {
      color: 'var(--primary-blue)',
    },
  }

  const onTabSelect = useCallback(() => {
    onSelectTab(value)
  }, [value, onSelectTab])

  return (
    <styled.div
      data-state={isSelected ? 'Selected' : 'Default'}
      style={tabStyle}
      onClick={onTabSelect}
    >
      <Text.Medium style={textStyle}>{label}</Text.Medium>
    </styled.div>
  )
}

export const TabsGroup = <T extends string>({
  selectedTab,
  onSelectTab,
  style,
  tabs,
  maxVisibleTabs = DEFAULT_MAX_VISIBLE_TABS,
  moreDropdownLabel,
}: TabsGroupProps<T>) => {
  const t = useTranslations().app.common
  const moreLabel = moreDropdownLabel ?? t.more
  const resolvedTabs = useMemo(
    () => tabs.filter((item) => item.isVisible !== false),
    [tabs],
  )
  const allKeys = useMemo(
    () => resolvedTabs.map((tab) => tab.value),
    [resolvedTabs],
  )
  const needsOverflow = allKeys.length > maxVisibleTabs

  const getLabel = useCallback(
    (key: T) => tabs.find((tab) => tab.value === key)?.label ?? String(key),
    [tabs],
  )

  const [visibleKeys, setVisibleKeys] = useState<T[]>(() =>
    needsOverflow ? allKeys.slice(0, maxVisibleTabs - 1) : allKeys,
  )

  const dropdownKeys = useMemo(
    () => allKeys.filter((k) => !visibleKeys.includes(k)),
    [allKeys, visibleKeys],
  )

  useEffect(() => {
    if (allKeys.length <= maxVisibleTabs) return
    if (!dropdownKeys.includes(selectedTab)) return
    setVisibleKeys((prev) => [...prev.slice(0, -1), selectedTab])
  }, [selectedTab, dropdownKeys, allKeys.length, maxVisibleTabs])

  useEffect(() => {
    if (allKeys.length <= maxVisibleTabs) {
      setVisibleKeys(allKeys)
      return
    }
    setVisibleKeys(allKeys.slice(0, maxVisibleTabs - 1))
  }, [allKeys, maxVisibleTabs])

  const handleDropdownSelect = useCallback(
    (key: T) => {
      setVisibleKeys((prev) => [...prev.slice(0, -1), key])
      onSelectTab(key)
    },
    [onSelectTab],
  )

  const visibleTabItems = useMemo(
    () =>
      visibleKeys
        .map((key) => resolvedTabs.find((tab) => tab.value === key))
        .filter((tab): tab is TabItem<T> => tab != null),
    [visibleKeys, resolvedTabs],
  )

  return (
    <TabRow selectedTab={selectedTab} style={style}>
      {visibleTabItems.map((item) => (
        <Tab
          key={item.value}
          value={item.value}
          label={item.label}
          onSelectTab={onSelectTab}
        />
      ))}
      {needsOverflow && (
        <MoreDropdownTab
          label={moreLabel}
          dropdownKeys={dropdownKeys}
          selectedTab={selectedTab}
          onSelectTab={handleDropdownSelect}
          getLabel={getLabel}
        />
      )}
    </TabRow>
  )
}
